import { useEffect, useMemo, useRef, useState } from "react";

import {
  ACTIVITY_LABELS,
  REASON_LABELS,
  importEmail,
  logTime,
  suggestCases,
  type ActivityType,
  type AlreadyArchived,
  type AttachmentBlob,
  type CaseSuggestion,
  type FilingCase,
  type ImportEmailPayload,
  type SiggeCase,
  type SuggestResponse,
} from "./api";
import { signInWithOffice, tokenStore, type SiggeUser } from "./auth";
import {
  loadActiveCases,
  localSearchCases,
  markFiledInOutlook,
} from "./cache";
import {
  formatDuration,
  formatTime,
  loadTimer,
  newTimer,
  removeSuspected,
  runningDurationMs,
  saveTimer,
  stopTimer,
  tickHeartbeat,
  toggleSegment,
  totalIncludedMinutes,
  TIMER_CONSTANTS,
  type TimerState,
} from "./timer";
import {
  loadSettings,
  saveSettings,
  type AddinSettings,
} from "./settings";

type Status =
  | { kind: "loading" }
  | { kind: "signin" }
  | { kind: "ready"; user: SiggeUser; mail: MailContext }
  | { kind: "error"; message: string };

interface MailContext {
  subject: string;
  from: { email: string; name: string };
  to: { email: string; name: string }[];
  cc: { email: string; name: string }[];
  date: string | null;
  messageId: string;
  conversationId: string;
  body: { text: string; html: string };
  // Manifestet låser till MessageRead, så attachments är alltid AttachmentDetails.
  attachments: Office.AttachmentDetails[];
}

export function App() {
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const userRef = useRef<SiggeUser | null>(null);

  useEffect(() => {
    void initialise(setStatus);
  }, []);

  // Håll en ref till user så ItemChanged-handlern inte behöver re-bindas
  // varje gång mail uppdateras.
  useEffect(() => {
    if (status.kind === "ready") userRef.current = status.user;
  }, [status]);

  // Pinned mode: när användaren byter mejl med pane:n öppen → ladda om
  // mail-context och låt suggest-loopen re-fyra. Token sitter kvar.
  useEffect(() => {
    if (status.kind !== "ready") return;
    const handler = () => {
      const user = userRef.current;
      if (!user) return;
      loadMailContext()
        .then((mail) => setStatus({ kind: "ready", user, mail }))
        .catch((err: Error) =>
          setStatus({ kind: "error", message: err.message })
        );
    };
    Office.context.mailbox.addHandlerAsync(
      Office.EventType.ItemChanged,
      handler,
      () => undefined
    );
    return () => {
      // Office.js stödjer inte att ta bort en *specifik* handler — anropet
      // tar bort alla handlers för event-typen. OK för oss eftersom det
      // bara finns en.
      Office.context.mailbox.removeHandlerAsync(
        Office.EventType.ItemChanged,
        () => undefined
      );
    };
    // Vi vill bara binda vid övergången till "ready" — inte vid varje
    // mail-uppdatering. Därför endast status.kind i deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.kind]);

  if (status.kind === "loading") {
    return <Centered>Laddar…</Centered>;
  }
  if (status.kind === "error") {
    return (
      <div className="pane">
        <div className="error-box">{status.message}</div>
        <button
          className="btn btn-primary"
          onClick={() => initialise(setStatus)}
        >
          Försök igen
        </button>
      </div>
    );
  }
  if (status.kind === "signin") {
    return (
      <div className="pane">
        <h1>Sigge</h1>
        <p className="muted">
          Logga in med ditt 365-konto för att arkivera mejl till ärenden.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => initialise(setStatus, { force: true })}
        >
          Logga in via Microsoft
        </button>
      </div>
    );
  }

  return (
    <ReadyPane
      user={status.user}
      mail={status.mail}
      onSignOut={() => {
        tokenStore.clear();
        setStatus({ kind: "signin" });
      }}
    />
  );
}

async function initialise(
  setStatus: (s: Status) => void,
  { force = false }: { force?: boolean } = {}
): Promise<void> {
  setStatus({ kind: "loading" });
  try {
    const mail = await loadMailContext();

    if (!tokenStore.access || force) {
      const result = await signInWithOffice();
      setStatus({ kind: "ready", user: result.user, mail });
      return;
    }

    setStatus({
      kind: "ready",
      user: {
        id: 0,
        email: Office.context.mailbox.userProfile.emailAddress,
        full_name: Office.context.mailbox.userProfile.displayName,
        role: "lawyer",
      },
      mail,
    });
  } catch (err) {
    const msg = (err as Error).message || String(err);
    if (msg.toLowerCase().includes("inte inloggad")) {
      setStatus({ kind: "signin" });
    } else {
      setStatus({ kind: "error", message: msg });
    }
  }
}

async function loadMailContext(): Promise<MailContext> {
  const item = Office.context.mailbox.item;
  if (!item) throw new Error("Inget mejl är öppet.");

  const subject = ("subject" in item ? (item.subject as string) : "") || "";
  const conv = (item as Office.MessageRead).conversationId || "";
  const messageId =
    (item as Office.MessageRead).internetMessageId ||
    item.itemId ||
    "";

  const fromEmail = (item as Office.MessageRead).from?.emailAddress || "";
  const fromName = (item as Office.MessageRead).from?.displayName || "";
  const to = ((item as Office.MessageRead).to ?? []).map((r) => ({
    email: r.emailAddress,
    name: r.displayName || "",
  }));
  const cc = ((item as Office.MessageRead).cc ?? []).map((r) => ({
    email: r.emailAddress,
    name: r.displayName || "",
  }));
  const date = (item as Office.MessageRead).dateTimeCreated?.toISOString() ?? null;

  const body = await readBody(item);

  return {
    subject,
    from: { email: fromEmail, name: fromName },
    to,
    cc,
    date,
    messageId,
    conversationId: conv,
    body,
    attachments: (item as Office.MessageRead).attachments ?? [],
  };
}

function readBody(
  item: Office.MessageRead | Office.MessageCompose | Office.AppointmentRead
): Promise<{ text: string; html: string }> {
  return new Promise((resolve) => {
    if (!("body" in item)) return resolve({ text: "", html: "" });

    const out: { text: string; html: string } = { text: "", html: "" };
    item.body.getAsync(Office.CoercionType.Text, (textResult) => {
      if (textResult.status === Office.AsyncResultStatus.Succeeded) {
        out.text = textResult.value || "";
      }
      item.body.getAsync(Office.CoercionType.Html, (htmlResult) => {
        if (htmlResult.status === Office.AsyncResultStatus.Succeeded) {
          out.html = htmlResult.value || "";
        }
        resolve(out);
      });
    });
  });
}

type OutlookTab = "mail" | "timer" | "settings";

function ReadyPane({
  user,
  mail,
  onSignOut,
}: {
  user: SiggeUser;
  mail: MailContext;
  onSignOut: () => void;
}) {
  const [tab, setTab] = useState<OutlookTab>("mail");
  const [suggest, setSuggest] = useState<SuggestResponse | null>(null);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [suggestLoading, setSuggestLoading] = useState(true);
  const [activeCases, setActiveCases] = useState<FilingCase[]>([]);
  const [settings, setSettings] = useState<AddinSettings>(loadSettings);

  // Förladda + cacha aktiva ärenden så lokal fuzzy-sök är instant.
  // Körs en gång per session (ReadyPane mount); cachen själv har 5 min TTL
  // och revaliderar i bakgrunden.
  useEffect(() => {
    let cancelled = false;
    loadActiveCases()
      .then((cases) => {
        if (!cancelled) setActiveCases(cases);
      })
      .catch(() => {
        /* tyst — fall tillbaka på server-search via suggestCases */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const recipients = useMemo(() => {
    const all = [...mail.to, ...mail.cc].map((r) => r.email);
    return all.filter(
      (e) => e && e.toLowerCase() !== user.email.toLowerCase()
    );
  }, [mail.to, mail.cc, user.email]);

  useEffect(() => {
    let cancelled = false;
    setSuggestLoading(true);
    setSuggestError(null);
    suggestCases({
      senderEmail: mail.from.email,
      recipients,
      outlookConversationId: mail.conversationId,
      outlookMessageId: mail.messageId,
      subject: mail.subject,
    })
      .then((res) => {
        if (!cancelled) setSuggest(res);
      })
      .catch((err: Error) => {
        if (!cancelled) setSuggestError(err.message);
      })
      .finally(() => {
        if (!cancelled) setSuggestLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    mail.from.email,
    mail.conversationId,
    mail.messageId,
    mail.subject,
    recipients,
  ]);

  return (
    <div className="pane">
      <header className="pane-head">
        <div>
          <div className="muted small">Inloggad som</div>
          <div>{user.email}</div>
        </div>
        <button className="btn-link" onClick={onSignOut}>
          Logga ut
        </button>
      </header>

      <div className="tab-bar">
        <button
          className={tab === "mail" ? "tab is-on" : "tab"}
          onClick={() => setTab("mail")}
        >
          Mejl
        </button>
        {settings.timerEnabled && (
          <button
            className={tab === "timer" ? "tab is-on" : "tab"}
            onClick={() => setTab("timer")}
          >
            Timer
          </button>
        )}
        <button
          className={tab === "settings" ? "tab is-on" : "tab"}
          onClick={() => setTab("settings")}
        >
          Inställningar
        </button>
      </div>

      {tab === "mail" && (
        <UnifiedPanel
          user={user}
          mail={mail}
          suggest={suggest}
          suggestLoading={suggestLoading}
          suggestError={suggestError}
          activeCases={activeCases}
        />
      )}
      {tab === "timer" && settings.timerEnabled && (
        <TimerTab activeCases={activeCases} />
      )}
      {tab === "settings" && (
        <SettingsTab
          settings={settings}
          onChange={(next) => {
            setSettings(next);
            saveSettings(next);
          }}
        />
      )}
    </div>
  );
}

/**
 * Merged filing + time-log panel. Tidigare två tabs ("Arkivera" och
 * "Logga tid") — juristen tvingades välja ärende två gånger och byta
 * flik för det som alltid är samma handling: sortera det öppna mejlet
 * mot rätt ärende och notera hur lång tid det tog.
 *
 * Ny flow:
 *   1. Välj ärende (från förslagen eller manuell sök) — en gång.
 *   2. Fyll i minuter om tiden ska loggas (annars lämna tomt).
 *   3. Klicka "Arkivera" — mejlet importeras alltid; om minuter > 0
 *      loggas tid i samma requestpar.
 *
 * Time-fälten är alltid synliga men kräver ingen input; success-vyn
 * berättar tydligt vilka delar som utfördes.
 */
function UnifiedPanel({
  user,
  mail,
  suggest,
  suggestLoading,
  suggestError,
  activeCases,
}: {
  user: SiggeUser;
  mail: MailContext;
  suggest: SuggestResponse | null;
  suggestLoading: boolean;
  suggestError: string | null;
  activeCases: FilingCase[];
}) {
  const defaultCase = useMemo<SiggeCase | null>(() => {
    if (suggest?.already_archived) {
      return {
        id: suggest.already_archived.case_id,
        title: suggest.already_archived.case_title,
        status: "",
        client: null,
      };
    }
    return suggest?.suggestions[0]?.case ?? null;
  }, [suggest]);

  const [selected, setSelected] = useState<SiggeCase | null>(null);
  const [query, setQuery] = useState("");
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [activity, setActivity] = useState<ActivityType>("radgivning");
  // Minuter börjar tomt — juristen väljer aktivt att logga tid genom
  // att fylla i ett värde. Tomt = arkivera bara, ingen tidspost skapas.
  const [minutes, setMinutes] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [billable, setBillable] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{
    caseTitle: string;
    attachmentCount: number;
    loggedMinutes: number | null;
  } | null>(null);

  useEffect(() => {
    if (defaultCase && !selected) setSelected(defaultCase);
  }, [defaultCase, selected]);

  const manualCases = useMemo(
    () => localSearchCases(activeCases, query),
    [activeCases, query]
  );

  const submit = async () => {
    if (!selected) {
      setError("Välj ett ärende först.");
      return;
    }
    // Minuter är valfria — vi validerar bara OM något är ifyllt.
    const trimmed = minutes.trim();
    const wantsTime = trimmed !== "" && trimmed !== "0";
    let mins = 0;
    if (wantsTime) {
      mins = parseInt(trimmed, 10);
      if (Number.isNaN(mins) || mins <= 0) {
        setError("Minuter måste vara ett positivt tal, eller lämnas tomt.");
        return;
      }
    }

    setBusy(true);
    setError(null);
    try {
      const attachments = includeAttachments
        ? await collectAttachments(mail.attachments)
        : [];
      const payload: ImportEmailPayload = {
        case: selected.id,
        direction:
          mail.from.email.toLowerCase() === user.email.toLowerCase()
            ? "outbound"
            : "inbound",
        subject: mail.subject,
        sender_email: mail.from.email,
        sender_name: mail.from.name,
        recipients: mail.to,
        cc: mail.cc,
        sent_at: mail.date,
        body_text: mail.body.text,
        body_html: mail.body.html,
        outlook_message_id: mail.messageId,
        outlook_conversation_id: mail.conversationId,
        // Stage 7 — user:ns senaste val (default "auto"). Backend
        // routar mode:n vidare till orchestratorn för bilagor.
        // Läses direkt från localStorage här; UnifiedPanel har inte
        // settings-state:n som prop och behöver inte re-rendera på
        // ändring — vi vill bara ha det senaste värdet vid submit.
        ingestion_mode: loadSettings().lastIngestionMode,
      };
      await importEmail(payload, attachments);
      // Markera i Outlook-mejllistan så det syns att det är arkiverat.
      void markFiledInOutlook();

      // Tidsloggen körs EFTER lyckad arkivering — så en arkiverings-
      // fail inte producerar en förkeld tidspost. Om time-anropet
      // failar rapporterar vi det men arkiveringen står ändå kvar.
      if (wantsTime) {
        await logTime({
          case: selected.id,
          activity_type: activity,
          duration_minutes: mins,
          note,
          billable,
        });
      }

      setDone({
        caseTitle: selected.title,
        attachmentCount: includeAttachments ? mail.attachments.length : 0,
        loggedMinutes: wantsTime ? mins : null,
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="tab-pane">
        <div className="success-box">
          ✓ Mejl arkiverat till <strong>{done.caseTitle}</strong>
          {done.attachmentCount > 0 && (
            <> tillsammans med {done.attachmentCount} bilag(or)</>
          )}
          .
          {done.loggedMinutes !== null && (
            <>
              {" "}
              <br />
              ✓ {done.loggedMinutes} min loggat på samma ärende.
            </>
          )}
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            setDone(null);
            setNote("");
            setMinutes("");
          }}
        >
          Arkivera fler
        </button>
      </div>
    );
  }

  const already = suggest?.already_archived ?? null;
  const suggestions = suggest?.suggestions ?? [];

  return (
    <div className="tab-pane">
      {already && <AlreadyArchivedBanner already={already} />}

      {/* Steg 1: välj ärende. När inget är valt visas förslag +
          manuell sök. När ett är valt visas det + "Byt"-länk. */}
      <div className="field">
        <label className="field-label">Ärende</label>
        {selected ? (
          <div className="selected-case">
            <div>
              <div className="case-title">{selected.title}</div>
              <div className="muted small">#{selected.id}</div>
            </div>
            <button
              className="btn-link"
              onClick={() => {
                setSelected(null);
                setQuery("");
              }}
              disabled={busy}
            >
              Byt
            </button>
          </div>
        ) : (
          <>
            <section>
              <div className="section-label">Förslag</div>
              {suggestLoading && (
                <div className="muted small">Letar förslag…</div>
              )}
              {suggestError && (
                <div className="error-box">{suggestError}</div>
              )}
              {!suggestLoading &&
                !suggestError &&
                suggestions.length === 0 && (
                  <div className="muted small">
                    Inga förslag hittades. Sök manuellt nedan.
                  </div>
                )}
              <div className="suggestion-list">
                {suggestions.map((s) => (
                  <SuggestionRow
                    key={s.case.id}
                    suggestion={s}
                    disabled={busy}
                    onClick={() => setSelected(s.case)}
                  />
                ))}
              </div>
            </section>

            <section>
              <div className="section-label">Eller sök ärende manuellt</div>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Sök efter titel, klient, jurist…"
                disabled={busy}
              />
              {query.trim() !== "" && (
                <div className="case-list">
                  {manualCases.length === 0 && (
                    <div className="muted small">Inga träffar.</div>
                  )}
                  {manualCases.map((c) => (
                    <button
                      key={c.id}
                      className="case-row"
                      disabled={busy}
                      onClick={() => setSelected(c)}
                    >
                      <div className="case-title">{c.title}</div>
                      <div className="muted small">
                        #{c.id} ·{" "}
                        {c.client?.full_name || c.client?.email || "—"}
                      </div>
                      <div className="case-action small">Välj →</div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {/* Resten av panelen är bara relevant när ett ärende är valt —
          fältet för attachments + tidsloggning + primärknappen.
          Vi renderar dem alltid men disable:ar dem tills ärende
          är valt så juristen ser vad som är på gång utan att kunna
          submitta halva formuläret. */}

      {mail.attachments.length > 0 && (
        <label className="toggle">
          <input
            type="checkbox"
            checked={includeAttachments}
            onChange={(e) => setIncludeAttachments(e.target.checked)}
            disabled={busy || !selected}
          />
          Inkludera {mail.attachments.length} bilag(or)
        </label>
      )}

      <div className="field">
        <label className="field-label">Logga tid (valfritt)</label>
      </div>

      <div className="field">
        <label className="field-label">Aktivitet</label>
        <select
          value={activity}
          onChange={(e) => setActivity(e.target.value as ActivityType)}
          disabled={busy || !selected}
        >
          {(Object.keys(ACTIVITY_LABELS) as ActivityType[]).map((k) => (
            <option key={k} value={k}>
              {ACTIVITY_LABELS[k]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field-label">Minuter</label>
        <div className="minutes-row">
          <input
            type="number"
            min={0}
            max={1440}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            placeholder="0 = ingen tid"
            disabled={busy || !selected}
          />
          <div className="quick-mins">
            {[15, 30, 60].map((m) => (
              <button
                key={m}
                className="chip"
                onClick={() => setMinutes(String(m))}
                disabled={busy || !selected}
              >
                {m} min
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="field">
        <label className="field-label">Anteckning</label>
        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="T.ex. Genomgång av mejlet"
          disabled={busy || !selected}
        />
      </div>

      <label className="toggle">
        <input
          type="checkbox"
          checked={billable}
          onChange={(e) => setBillable(e.target.checked)}
          disabled={busy || !selected}
        />
        Debiterbart
      </label>

      {error && <div className="error-box">{error}</div>}

      <button
        className="btn btn-primary btn-block"
        onClick={submit}
        disabled={busy || !selected}
      >
        {busy
          ? "Arkiverar…"
          : minutes.trim() && minutes.trim() !== "0"
            ? `Arkivera + logga ${minutes.trim()} min`
            : "Arkivera"}
      </button>
    </div>
  );
}

function AlreadyArchivedBanner({ already }: { already: AlreadyArchived }) {
  return (
    <div className="info-box">
      Detta mejl är redan arkiverat på{" "}
      <strong>{already.case_title}</strong> (#{already.case_id}). Att välja
      ärendet nedan arkiverar det igen utan dubbletter.
    </div>
  );
}

function SuggestionRow({
  suggestion,
  disabled,
  onClick,
}: {
  suggestion: CaseSuggestion;
  disabled: boolean;
  onClick: () => void;
}) {
  const c = suggestion.case;
  const reasons = suggestion.reasons.map(
    (r) => REASON_LABELS[r] || r
  );
  return (
    <button
      className="suggestion-row"
      onClick={onClick}
      disabled={disabled}
    >
      <div className="suggestion-main">
        <div className="case-title">{c.title}</div>
        <div className="muted small">
          #{c.id} · {c.client?.full_name || c.client?.email || "—"}
        </div>
        {reasons.length > 0 && (
          <div className="suggestion-reasons small">
            {reasons.slice(0, 3).join(" · ")}
          </div>
        )}
      </div>
      <div className="suggestion-action">Välj</div>
    </button>
  );
}

async function collectAttachments(
  attachments: Office.AttachmentDetails[]
): Promise<AttachmentBlob[]> {
  const out: AttachmentBlob[] = [];
  for (const a of attachments) {
    if (a.isInline) continue;
    const content = await fetchAttachmentContent(a.id);
    if (!content) continue;
    const blob = base64ToBlob(content, a.contentType || "application/octet-stream");
    out.push({
      name: a.name,
      contentType: a.contentType || "application/octet-stream",
      blob,
    });
  }
  return out;
}

function fetchAttachmentContent(attachmentId: string): Promise<string | null> {
  return new Promise((resolve) => {
    const item = Office.context.mailbox.item;
    if (!item || !("getAttachmentContentAsync" in item)) return resolve(null);
    (item as Office.MessageRead).getAttachmentContentAsync(attachmentId, (result) => {
      if (
        result.status === Office.AsyncResultStatus.Succeeded &&
        result.value?.format === Office.MailboxEnums.AttachmentContentFormat.Base64
      ) {
        resolve(result.value.content);
      } else {
        resolve(null);
      }
    });
  });
}

function base64ToBlob(b64: string, contentType: string): Blob {
  const byteChars = atob(b64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    bytes[i] = byteChars.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType });
}

/**
 * Timer-tab för Outlook — samma UX som Word:s: heartbeat-baserad
 * segment-detektering + Granska aktivitet. Skiljer sig genom att
 * ärendet inte är förvalt av mail-context; juristen väljer manuellt
 * bland cachade aktiva ärenden. Timern behöver inget mail-kontext
 * alls (till skillnad från arkiverings-flödet på Mejl-fliken).
 *
 * Outlook-specifika begränsningar:
 *   * Vi kan INTE spåra vilket mejl som är öppet när taskpanen är
 *     stängd/unpinnad — Office.EventType.ItemChanged fires inte då.
 *   * Detta är en manuell timer, inte per-mejl-tracking.
 */
function TimerTab({ activeCases }: { activeCases: FilingCase[] }) {
  const [state, setState] = useState<TimerState | null>(loadTimer);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [query, setQuery] = useState("");

  const results = useMemo(
    () => localSearchCases(activeCases, query),
    [activeCases, query]
  );
  const chosenCase = useMemo(() => {
    if (state?.caseId) {
      return (
        activeCases.find((c) => c.id === state.caseId) ??
        ({
          id: state.caseId,
          title: state.caseTitle ?? `#${state.caseId}`,
          status: "",
          client: null,
        } as SiggeCase)
      );
    }
    return null;
  }, [state?.caseId, state?.caseTitle, activeCases]);

  useEffect(() => {
    if (!state?.isRunning) return;
    const heartbeat = window.setInterval(() => {
      setState((prev) => {
        if (!prev || !prev.isRunning) return prev;
        const next = tickHeartbeat(prev);
        saveTimer(next);
        return next;
      });
    }, TIMER_CONSTANTS.HEARTBEAT_MS);
    const display = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => {
      window.clearInterval(heartbeat);
      window.clearInterval(display);
    };
  }, [state?.isRunning]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      setState((prev) => {
        if (!prev || !prev.isRunning) return prev;
        const next = tickHeartbeat(prev);
        saveTimer(next);
        return next;
      });
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Accepterar både SiggeCase (från chosenCase-memo:n) och FilingCase
  // (från cache-search:en). Vi använder bara id + title så en gemensam
  // strukturell typ räcker.
  const start = (c: { id: number; title: string }) => {
    const t = newTimer(c.id, c.title);
    saveTimer(t);
    setState(t);
    setReviewOpen(false);
    setError(null);
  };
  const stop = () => {
    setState((prev) => {
      if (!prev) return prev;
      const next = stopTimer(prev);
      saveTimer(next);
      return next;
    });
    setReviewOpen(true);
  };
  const logAndClose = async () => {
    if (!state) return;
    const total = totalIncludedMinutes(state);
    if (total <= 0) {
      setError("Ingen incheckad tid att logga.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await logTime({
        case: state.caseId!,
        activity_type: "radgivning",
        duration_minutes: total,
        note: state.note,
        billable: state.billable,
      });
      saveTimer(null);
      setState(null);
      setReviewOpen(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const disregard = () => {
    if (!state) return;
    if (state.segments.length > 0 || state.isRunning) {
      if (!window.confirm("Ta bort denna timer helt? Ingen tid loggas.")) {
        return;
      }
    }
    saveTimer(null);
    setState(null);
    setReviewOpen(false);
  };

  const suspectedCount = state?.segments.filter(
    (s) => s.suspected && !s.included
  ).length ?? 0;

  void tick;

  return (
    <div className="tab-pane">
      {chosenCase ? (
        <div className="muted small">
          Ärende: <strong>{chosenCase.title}</strong> · #{chosenCase.id}
        </div>
      ) : (
        <div className="field">
          <label className="field-label">Välj ärende</label>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Sök ärende…"
          />
          {query.trim() !== "" && (
            <ul className="case-list">
              {results.slice(0, 8).map((c) => (
                <li key={c.id}>
                  <button className="case-row" onClick={() => start(c)}>
                    <div className="case-title">{c.title}</div>
                    <div className="muted small">
                      #{c.id} ·{" "}
                      {c.client?.full_name || c.client?.email || "—"}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="timer-display">
        {formatDuration(state ? runningDurationMs(state) : 0)}
      </div>

      <div className="timer-actions">
        {state?.isRunning ? (
          <button className="btn btn-primary btn-block" onClick={stop}>
            Stoppa
          </button>
        ) : chosenCase && !state ? (
          <button
            className="btn btn-primary btn-block"
            onClick={() => start(chosenCase)}
          >
            Starta
          </button>
        ) : state && !state.isRunning ? (
          <button
            className="btn btn-primary btn-block"
            onClick={() => start(chosenCase!)}
          >
            Starta ny
          </button>
        ) : null}
      </div>

      {state && state.segments.length > 0 && (
        <details
          className="review-panel"
          open={reviewOpen}
          onToggle={(e) => setReviewOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary>
            Granska aktivitet
            {suspectedCount > 0 && (
              <span className="badge badge-warn">
                {suspectedCount} misstänkt{suspectedCount === 1 ? "" : "a"}
              </span>
            )}
          </summary>
          <ul className="segment-list">
            {state.segments.map((seg) => {
              const durMin = Math.round(
                (new Date(seg.endISO).getTime() -
                  new Date(seg.startISO).getTime()) /
                  60_000
              );
              return (
                <li
                  key={seg.id}
                  className={
                    seg.suspected
                      ? "segment-row is-suspected"
                      : "segment-row"
                  }
                >
                  <label className="segment-check">
                    <input
                      type="checkbox"
                      checked={seg.included}
                      onChange={() =>
                        setState((prev) => {
                          if (!prev) return prev;
                          const next = toggleSegment(prev, seg.id);
                          saveTimer(next);
                          return next;
                        })
                      }
                    />
                  </label>
                  <div className="segment-time">
                    {formatTime(seg.startISO)}–{formatTime(seg.endISO)}
                  </div>
                  <div className="segment-dur">{durMin} min</div>
                  <div className="segment-tag">
                    {seg.suspected ? "Misstänkt" : "Aktiv"}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="review-controls">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() =>
                setState((prev) => {
                  if (!prev) return prev;
                  const next = removeSuspected(prev);
                  saveTimer(next);
                  return next;
                })
              }
              disabled={suspectedCount === 0}
            >
              Avmarkera misstänkta
            </button>
            <div className="review-total">
              Totalt: <strong>{totalIncludedMinutes(state)} min</strong>
            </div>
          </div>
          <div className="field">
            <label className="field-label">Anteckning</label>
            <textarea
              rows={2}
              value={state.note}
              onChange={(e) =>
                setState((prev) => {
                  if (!prev) return prev;
                  const next = { ...prev, note: e.target.value };
                  saveTimer(next);
                  return next;
                })
              }
              placeholder="T.ex. Rådgivning per mejl"
            />
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={state.billable}
              onChange={(e) =>
                setState((prev) => {
                  if (!prev) return prev;
                  const next = { ...prev, billable: e.target.checked };
                  saveTimer(next);
                  return next;
                })
              }
            />
            Debiterbart
          </label>
        </details>
      )}

      {error && <div className="error-box">{error}</div>}

      {state && !state.isRunning && state.segments.length > 0 && (
        <div className="timer-final-actions">
          <button
            className="btn btn-primary btn-block"
            onClick={() => void logAndClose()}
            disabled={busy || totalIncludedMinutes(state) === 0}
          >
            {busy
              ? "Loggar…"
              : `Logga tid (${totalIncludedMinutes(state)} min)`}
          </button>
          <button
            className="btn btn-ghost btn-block"
            onClick={disregard}
            disabled={busy}
          >
            Ignorera
          </button>
        </div>
      )}

      {!state && !chosenCase && (
        <p className="muted small">
          Välj ett ärende ovan för att starta timern.
        </p>
      )}
    </div>
  );
}

/**
 * Per-enhet-inställningar för Outlook-add-in:et. Endast timer-toggle:n
 * är relevant här — Ctrl+S/auto-start är Word-specifika settings som
 * Outlook-flödet inte har någon motsvarighet till.
 */
function SettingsTab({
  settings,
  onChange,
}: {
  settings: AddinSettings;
  onChange: (next: AddinSettings) => void;
}) {
  return (
    <div className="tab-pane">
      <label className="settings-row">
        <input
          type="checkbox"
          checked={settings.timerEnabled}
          onChange={(e) =>
            onChange({ ...settings, timerEnabled: e.target.checked })
          }
        />
        <div>
          <div className="settings-label">Aktivera Timer-fliken</div>
          <div className="muted small">
            Manuell tidsspårning med start/stopp per ärende.
          </div>
        </div>
      </label>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="centered">{children}</div>;
}
